import React, { useEffect, useRef, useState, useCallback, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';

const IntegratedTerminalEditor = React.memo(forwardRef(({ setWriterRef, onSubmit, onContentChange, initialFileContent, filePath }, ref) => {
    const terminalRef = useRef(null); // Reference to the container element
    const xtermInstance = useRef(null); // Instance of xterm.js
    const fitAddon = useRef(null); // Instance of FitAddon for dynamic resizing
    const [mode, setMode] = useState('command'); // Mode: 'command' or 'editor'
    const [inputBuffer, setInputBuffer] = useState(''); // Buffer for command input
    const [prompt] = useState('$ '); // Terminal prompt
    const [editingFileName, setEditingFileName] = useState(null); // Current file being edited
    const [fileContentBuffer, setFileContentBuffer] = useState(''); // Content buffer for editor
    const [virtualFiles, setVirtualFiles] = useState({}); // Virtual file storage

    // Refs for latest state to handle async closures
    const latestInputBuffer = useRef('');
    useEffect(() => {
        latestInputBuffer.current = inputBuffer;
    }, [inputBuffer]);

    const latestFileContentBuffer = useRef('');
    useEffect(() => {
        latestFileContentBuffer.current = fileContentBuffer;
    }, [fileContentBuffer]);

    // Function to write text to the terminal
    const writeOutput = useCallback((text) => {
        if (xtermInstance.current) {
            xtermInstance.current.write(text.replace(/\n/g, '\r\n'));
        }
    }, []);

    // Reset the input line and write a new prompt
    const resetPrompt = useCallback(() => {
        if (xtermInstance.current) {
            xtermInstance.current.write('\r\n');
            xtermInstance.current.write(prompt);
        }
    }, [prompt]);

    // Fit the terminal to its container size
    const fitTerminal = useCallback(() => {
        if (fitAddon.current && xtermInstance.current && terminalRef.current) {
            try {
                // Check if the terminal container is visible and has dimensions
                const rect = terminalRef.current.getBoundingClientRect();
                if (rect.width > 0 && rect.height > 0) {
                    fitAddon.current.fit();
                }
            } catch (e) {
                console.error("Error fitting terminal:", e);
            }
        }
    }, []);

    useImperativeHandle(ref, () => ({
        fit: fitTerminal
    }));

    // Initialize the terminal with xterm.js and FitAddon
    const initializeTerminal = useCallback(() => {
        if (!xtermInstance.current && terminalRef.current) {
            const term = new Terminal({
                cursorBlink: true,
                cursorStyle: 'block',
                theme: {
                    background: '#000',
                    foreground: '#0f0',
                    cursor: '#0f0',
                    selectionBackground: '#61dafb',
                    selectionForeground: '#1e1e1e',
                },
                convertEol: true,
            });
            const fit = new FitAddon();
            term.loadAddon(fit);
            term.open(terminalRef.current);
            xtermInstance.current = term;
            fitAddon.current = fit;

            // The fit will now be called from the parent component
            term.write(prompt);

            term.onData(e => {
                if (xtermInstance.current) {
                    if (e === '\r') {
                        xtermInstance.current.write('\r\n');
                        const command = latestInputBuffer.current.trim();
                        console.log("IntegratedTerminalEditor: Commande saisie:", command);

                        if (command.startsWith('vi ') || command.startsWith('nano ')) {
                            const fileName = command.split(' ')[1];
                            if (fileName) {
                                setEditingFileName(fileName);
                                setFileContentBuffer(virtualFiles[fileName] || '');
                                setMode('editor');
                                writeOutput(`Ouverture de l'éditeur pour ${fileName}. Appuyez sur Ctrl+S (sauvegarder) ou Ctrl+D (annuler).\r\n`);
                            } else {
                                writeOutput('Usage: vi <filename> ou nano <filename>\r\n');
                                resetPrompt();
                            }
                        } else if (command.startsWith('cat ')) {
                            const fileName = command.split(' ')[1];
                            if (fileName && virtualFiles[fileName]) {
                                writeOutput(`\r\n${virtualFiles[fileName]}\r\n`);
                                resetPrompt();
                            } else if (fileName) {
                                writeOutput(`Erreur: Fichier '${fileName}' non trouvé.\r\n`);
                                resetPrompt();
                            } else {
                                writeOutput('Usage: cat <filename>\r\n');
                                resetPrompt();
                            }
                        } else {
                            let fileRef = null;
                            const fileMatch = command.match(/(-f|--filename)\s+([a-zA-Z0-9_.-]+)/);
                            if (fileMatch && virtualFiles[fileMatch[2]]) {
                                fileRef = {
                                    name: fileMatch[2],
                                    content: virtualFiles[fileMatch[2]]
                                };
                                console.log(`IntegratedTerminalEditor: Commande ${command} référence fichier ${fileRef.name}`);
                            }
                            onSubmit(command, writeOutput, fileRef);
                        }
                        setInputBuffer('');
                    } else if (e === '\x7f') {
                        if (latestInputBuffer.current.length > 0) {
                            xtermInstance.current.write('\b \b');
                            setInputBuffer(prev => prev.slice(0, -1));
                        }
                    } else if (e === 'OP' || e === '[A' || e === '[B' || e === '[C' || e === '[D') {
                        // Absorb arrow keys and other special sequences to prevent them from breaking the input
                        return;
                    } else {
                        xtermInstance.current.write(e);
                        setInputBuffer(prev => prev + e);
                    }
                }
            });
        }
    }, [prompt, onSubmit, virtualFiles, writeOutput, resetPrompt]);

    // Initialize terminal on mount with a delay to ensure DOM readiness
    useLayoutEffect(() => {
        if (setWriterRef) {
            setWriterRef({
                write: writeOutput,
                resetPrompt: resetPrompt,
                fitTerminal: fitTerminal
            });
        }
        const timer = setTimeout(() => {
            initializeTerminal();
        }, 200);
        return () => {
            clearTimeout(timer);
            if (xtermInstance.current) {
                xtermInstance.current.dispose();
                xtermInstance.current = null;
            }
            if (fitAddon.current) {
                fitAddon.current = null;
            }
        };
    }, [prompt, setWriterRef, onSubmit, writeOutput, resetPrompt, initializeTerminal, fitTerminal]);

    // Handle resize events to adjust terminal size with robust checks
    useEffect(() => {
        let resizeTimeout;

        const resizeObserver = new ResizeObserver(entries => {
            // Débounce les appels de resize pour éviter les loops
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }

            resizeTimeout = setTimeout(() => {
                if (mode === 'command' && fitAddon.current && xtermInstance.current && terminalRef.current) {
                    try {
                        // Check if the terminal container is visible before fitting
                        const rect = terminalRef.current.getBoundingClientRect();
                        if (rect.width > 0 && rect.height > 0) {
                            fitAddon.current.fit();
                        }
                    } catch (error) {
                        console.error("Error during terminal resize:", error);
                    }
                }
            }, 100); // Débounce de 100ms
        });

        if (terminalRef.current && mode === 'command') {
            try {
                resizeObserver.observe(terminalRef.current);
            } catch (error) {
                console.error("Error setting up ResizeObserver:", error);
            }
        }

        return () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            try {
                resizeObserver.disconnect();
            } catch (error) {
                console.error("Error disconnecting ResizeObserver:", error);
            }
        };
    }, [mode]);

    // Note: handleCommandInput function removed as it's not used in the current xterm implementation

    // Gère la saisie dans l'éditeur de fichier
    const handleFileEditorChange = useCallback((e) => {
        setFileContentBuffer(e.target.value);
    }, []);

    // Gère les raccourcis clavier dans l'éditeur (Ctrl+S pour sauvegarder, Ctrl+D pour annuler)
    const handleEditorKeyDown = useCallback((e) => {
        if (e.ctrlKey && e.key === 's') {
            e.preventDefault(); // Empêche la sauvegarde par défaut du navigateur
            setVirtualFiles(prev => ({ ...prev, [editingFileName]: latestFileContentBuffer.current }));
            writeOutput(`\r\nFichier '${editingFileName}' sauvegardé temporairement.\r\n`);
            setMode('command');
            setEditingFileName(null);
            setFileContentBuffer('');
            resetPrompt();
        } else if (e.ctrlKey && e.key === 'd') {
            e.preventDefault(); // Empêche l'action par défaut du navigateur
            writeOutput(`\r\nÉdition de '${editingFileName}' annulée.\r\n`);
            setMode('command');
            setEditingFileName(null);
            setFileContentBuffer('');
            resetPrompt();
        }
    }, [editingFileName, writeOutput, resetPrompt, latestFileContentBuffer]);

    return (
        <div className="flex flex-col h-full bg-black text-white font-mono text-sm p-2">
            {mode === 'command' && (
                <div ref={terminalRef} className="w-full h-full" />
            )}
            {mode === 'editor' && (
                <div className="flex flex-col h-full">
                    <div className="bg-gray-700 text-white p-1">
                        Édition de: {editingFileName} (Ctrl+S: Sauvegarder, Ctrl+D: Annuler)
                    </div>
                    <textarea
                        value={fileContentBuffer}
                        onChange={handleFileEditorChange}
                        onKeyDown={handleEditorKeyDown}
                        className="flex-grow w-full bg-black text-green-400 p-2 border-0 focus:outline-none"
                        autoFocus
                    />
                </div>
            )}
        </div>
    );
}));

export default IntegratedTerminalEditor;
